import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DropdownValue, InputSize } from 'components/Dropdown/types';
import { setErrorMessages } from 'components/ErrorMessageHandler/slice';
import { useAppDispatch } from 'store/hooks';
import { errors, placeHolders } from './messages';
import {
  setPrintBtnLoading,
  setPrintBtnDisabled,
  setSelectedCIBAccount
} from './slice';
import { StyledCIBDropdown } from './styled';
import { PrintChecksCIBDropdownType } from './types';
import { usePrintChecksCIBData } from './usePrintChecksCIBData';
import {
  createCashInBankDropdownOptions,
  createLabel,
  createCashInBankDropdownValue
} from './utils';

export const PrintChecksCIBDropdown = (): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const { data, isLoading } = usePrintChecksCIBData();
  const intl = useIntl();
  const [cashInBankAccount, setCashInBankAccount] = useState<DropdownValue>();

  const cashInBankAccountsOptions = createCashInBankDropdownOptions(data);

  const handleCashInBankAccounts = (selectedValue: DropdownValue) => {
    setCashInBankAccount(selectedValue);
    const selectedCIBAccount = data?.find(
      (cashInBankAcc: PrintChecksCIBDropdownType) =>
        createLabel(cashInBankAcc.accountNumber, cashInBankAcc.accountName) ===
        selectedValue.label
    );
    if (selectedCIBAccount) {
      dispatch(setSelectedCIBAccount(selectedCIBAccount));
    }
  };

  useEffect(() => {
    if (isLoading) {
      dispatch(setPrintBtnLoading(true));
    }
    dispatch(setPrintBtnLoading(false));
  }, [isLoading, dispatch]);

  useEffect(() => {
    if (data && data.length === 0) {
      dispatch(setPrintBtnDisabled(true));
      dispatch(
        setErrorMessages([intl.formatMessage(errors.cibAccountRequired)])
      );
    } else {
      dispatch(setPrintBtnDisabled(false));
    }
  }, [data, dispatch, intl]);

  useEffect(() => {
    if (data?.length) {
      const defaultValue = data.find(cib => cib.index === 0) || data[0];
      if (defaultValue) {
        setCashInBankAccount(createCashInBankDropdownValue(defaultValue));
        dispatch(setSelectedCIBAccount(defaultValue));
      }
    } else {
      setCashInBankAccount({
        label: '',
        value: ''
      });
    }
  }, [data, setCashInBankAccount, dispatch]);

  return (
    <StyledCIBDropdown
      inputId="printChecksCIBAccounts"
      dataTestId="printChecksCIBAccounts"
      label={
        <FormattedMessage
          description="Label for Cash In Bank Accounts dropdown"
          defaultMessage="CIB Account"
        />
      }
      placeHolder={intl.formatMessage(placeHolders.placeHolderSelect)}
      size={InputSize.SMALL}
      onChange={handleCashInBankAccounts}
      options={cashInBankAccountsOptions || []}
      value={cashInBankAccount}
      isLoading={isLoading}
      isRequired
      errorMessage={
        <FormattedMessage
          description="Label for selecting a Cash In Bank Account"
          defaultMessage="CIB Account is required"
        />
      }
    />
  );
};
